import {
  filter,
  firstValueFrom,
  from,
  type Observable,
  repeat,
  retry,
  timeout,
  TimeoutError,
  delay,
} from 'rxjs'
import { getHttpErrorConstructorForStatusCode } from 'fejl'
import { toURLSearchParams } from '@/utils/url-util'
import { share, switchMap, take } from 'rxjs/operators'
import { isOnline$ } from '@/utils/browser-util'
import { createAbortError, onAbort } from '@/utils/abort-util'

/**
 * Options for constructing an API client.
 */
export interface ApiClientOptions {
  /**
   * The API base URL.
   */
  readonly baseURL: string

  /**
   * A stream of tokens.
   */
  readonly token$: Observable<string>
}

/**
 * Resolves a path-based base URL against the current origin.
 * Absolute base URLs are returned as-is.
 *
 * @param baseURL
 */
export function resolveBaseURL(baseURL: string): string {
  return baseURL?.startsWith('/')
    ? baseURL.endsWith('/')
      ? `${location.origin}${baseURL}`
      : `${location.origin}${baseURL}/`
    : baseURL
}

/**
 * Base API client.
 */
export class ApiClient {
  protected readonly options: ApiClientOptions

  /**
   * Constructor.
   *
   * @param options
   */
  constructor(options: ApiClientOptions) {
    this.options = {
      ...options,
      baseURL: resolveBaseURL(options.baseURL),
    }
  }

  /**
   * Sends a GET request.
   */
  protected async _get<R>(
    resource: string,
    params?: URLSearchParams,
    init?: RequestInit,
    options?: SendRequestOptions,
  ): Promise<R> {
    const request = new Request(this._createURL(resource, params), {
      ...init,
      method: 'GET',
    })
    return await this._json(request, options)
  }

  /**
   * Sends a POST request.
   */
  protected _post<T, R>(
    resource: string,
    body: T,
    params?: URLSearchParams,
    init?: RequestInit,
    options?: SendRequestOptions,
  ): Promise<R> {
    return this._method<T, R>('POST', resource, body, params, init, options)
  }

  /**
   * Sends a PUT request.
   */
  protected _put<T, R>(
    resource: string,
    body: T,
    params?: URLSearchParams,
    init?: RequestInit,
    options?: SendRequestOptions,
  ): Promise<R> {
    return this._method<T, R>('PUT', resource, body, params, init, options)
  }

  /**
   * Sends a PATCH request.
   */
  protected _patch<T, R>(
    resource: string,
    body: T,
    params?: URLSearchParams,
    init?: RequestInit,
    options?: SendRequestOptions,
  ): Promise<R> {
    return this._method<T, R>('PATCH', resource, body, params, init, options)
  }

  /**
   * Sends a DELETE request.
   *
   */
  protected _delete<T, R>(
    resource: string,
    body?: T,
    params?: URLSearchParams,
    init?: RequestInit,
    options?: SendRequestOptions,
  ): Promise<R> {
    return this._method<T, R>('DELETE', resource, body, params, init, options)
  }

  /**
   * Makes a request with the current token, if we have one.
   */
  protected async _send(
    request: Request,
    options?: SendRequestOptions,
  ): Promise<Response> {
    if (options?.unauthenticated) {
      return fetch(request)
    }

    const augmentedRequest = await this._withAuthentication(request)

    return fetch(augmentedRequest)
  }

  /**
   * Makes a request using {@link ApiClient._send} and returns the response content
   * as an object deserialized from JSON.
   *
   * @param request
   * @param options
   */
  protected async _json<T>(
    request: Request,
    options?: SendRequestOptions,
  ): Promise<T> {
    return await this._send(request, options)
      .then(ApiClient.throwIfNotSuccessful)
      .then((r) => {
        // Don't parse as json if the response is empty
        if (r.status === 204) {
          return Symbol('empty http response') as T
        }

        // Several of our APIs answer `200` with an empty or plain-text body
        // (IAM's workspace login sends "OK"), which `r.json()` rejects.
        const isJson = r.headers.get('Content-Type')?.includes('json')
        return r
          .text()
          .then(
            (text) =>
              (isJson && text ? JSON.parse(text) : text || undefined) as T,
          )
      })
  }

  /**
   * Creates the URL for a resource, with the given params.
   */
  protected _createURL(resource: string, params?: URLSearchParams): URL {
    const url = new URL(resource, this.options.baseURL)
    if (params) {
      url.search = params.toString()
    }

    return url
  }

  /**
   * Creates a copy of {@link request} and adds an `Authorization` header
   * with a token.
   *
   * @param request
   * @private
   */
  private async _withAuthentication(request: Request) {
    // Clone the input request so we can add more stuff to it.
    const augmentedRequest = request.clone()

    if (!augmentedRequest.headers.has('Authorization')) {
      const token = await this._getToken()
      augmentedRequest.headers.set('Authorization', `Bearer ${token}`)
    }
    return augmentedRequest
  }

  /**
   * A simple `fetch`-compatible method that uses XHR under the hood so we can
   * attach progress listeners.
   * This should only be used when submitting {@link FormData}
   * via the XHR {@link options} parameter
   *
   * @param request The `fetch` request which will be mapped to XHR.
   * @param options Options for the request. Use the {@link XhrSendRequestOptions.form}
   *  property to specify the form data.
   */
  async _xhr(
    request: Request,
    options?: XhrSendRequestOptions,
  ): Promise<Response> {
    if (!options?.unauthenticated) {
      request = await this._withAuthentication(request)
    }

    return sendXhrRequest(request, options)
  }

  /**
   * Shortcut for making a JSON-bodied HTTP request.
   */
  private async _method<T, R>(
    method: string,
    resource: string,
    body: T | undefined,
    params: URLSearchParams | undefined,
    init: RequestInit | undefined,
    options?: SendRequestOptions,
  ): Promise<R> {
    const url = this._createURL(resource, params)

    const rawBody =
      init?.body ?? (body === undefined ? undefined : JSON.stringify(body))

    const request = new Request(url, {
      ...init,
      method,
      body: rawBody,
      headers: {
        // Only set content type if we're sending JSON.
        ...(body === undefined
          ? undefined
          : { 'Content-Type': 'application/json' }),
        ...init?.headers,
      },
    })

    return await this._json(request, options)
  }

  /**
   * Subscribes to the token observable and waits for it to emit a token.
   * If it does not emit a token within a short period of time, the
   * returned promise is rejected.
   * @protected
   */
  protected async _getToken(): Promise<string> {
    try {
      return await firstValueFrom(this.options.token$.pipe(timeout(10_000)))
    } catch (err: unknown) {
      if (err instanceof TimeoutError) {
        throw new Error('Timed out waiting for a valid token', { cause: err })
      }
      throw err
    }
  }

  /**
   * Connects to Socket.IO and emits realtime events.
   *
   * @protected
   */
  protected _events<
    T extends { type: string; data: unknown },
  >(): Observable<T> {
    return createRealtimeEvents<T>(this.options)
  }

  /**
   * Throws if the response is not successful.
   *
   * @param response
   */
  static async throwIfNotSuccessful(
    this: void,
    response: Response,
  ): Promise<Response> {
    if (response.ok) {
      return response
    }

    // Error responses from proxies and some of our APIs have no JSON body;
    // the status code alone must still produce an HTTP error.
    const body = (await response.json().catch(() => null)) as Record<
      string,
      unknown
    > | null
    const { message: messageRaw, ...responseData } = body ?? {}
    const message =
      typeof messageRaw === 'string'
        ? messageRaw
        : 'An error occurred when communicating with the server'
    const ErrorClass = getHttpErrorConstructorForStatusCode(response.status)
    throw new ErrorClass(message, {
      statusCode: response.status,
      ...responseData,
    })
  }

  /**
   * Creates a URLSearchParams object from a page query.
   *
   * @param query
   * @protected
   */
  protected static createSearchParamsFromPageQuery(
    query: CursorPageQueryParams,
  ) {
    return toURLSearchParams({ ...query })
  }
}

/**
 * Connects to Socket.IO and emits realtime events.
 *
 * @param options
 */
export function createRealtimeEvents<T extends { type: string; data: unknown }>(
  options: ApiClientOptions,
): Observable<T> {
  return options.token$.pipe(
    take(1),
    switchMap((token) =>
      // Using `import` here lets us defer loading socket.io
      // until we're ready to connect.
      from(import('./socket-io-realtime')).pipe(
        switchMap((module) =>
          module.connectToSocketIoRealtimeEvents({
            token,
            baseURL: resolveBaseURL(options.baseURL),
          }),
        ),
      ),
    ),
    repeat(),
    // @ts-ignore: go home typescript, ur drunk
    retry({
      // Only retry while we're online.
      delay: () => isOnline$.pipe(filter(Boolean), delay(2_000)),
      resetOnSuccess: true,
    }),
    share(),
  )
}

/**
 * A simple `fetch`-compatible function that uses XHR under the hood so we can
 * attach progress listeners.
 * This should only be used when submitting {@link FormData}
 * via the XHR {@link options} parameter.
 *
 * @param request The `fetch` request which will be mapped to XHR.
 * @param options Options for the request. Use the {@link XhrSendRequestOptions.form}
 *  property to specify the form data.
 */
export function sendXhrRequest(
  request: Request,
  options?: XhrSendRequestOptions,
): Promise<Response> {
  return new Promise<Response>((resolve, reject) => {
    if (request.signal.aborted) {
      reject(createAbortError('Network request aborted'))
      return
    }

    const xhr = new XMLHttpRequest()

    // Open the XMLHttpRequest with the method and URL from the Request.
    xhr.open(request.method || 'GET', request.url, true)

    onAbort(request.signal, () => {
      xhr.abort()
      reject(createAbortError('Network request aborted'))
    })

    // Transfer headers from the Request to the XMLHttpRequest.
    if (request.headers) {
      request.headers.forEach((value, key) => {
        xhr.setRequestHeader(key, value)
      })
    }

    // Handle network errors.
    xhr.onerror = () => reject(new TypeError('Network request failed'))
    xhr.ontimeout = () => reject(new TypeError('Network request failed'))

    // Set up a progress handler if provided.
    if (options?.onProgress) {
      const onProgress = options.onProgress
      xhr.upload.onprogress = (e) =>
        onProgress({ loaded: e.loaded, total: e.total })
    }

    // Setup our response handler.
    xhr.onload = () => {
      // Create a Headers object by parsing the raw response headers.
      const headers = new Headers()
      const headerStr = xhr.getAllResponseHeaders()
      headerStr
        .trim()
        .split(/[\r\n]+/)
        .forEach((line) => {
          const parts = line.split(': ')
          const header = parts.shift()
          const value = parts.join(': ')
          if (header) {
            headers.append(header, value)
          }
        })

      // Construct a Response object with the response text, status, and headers.
      const response = new Response(xhr.responseText, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: headers,
      })
      resolve(response)
    }

    if (options?.form) {
      xhr.send(options.form)
      return
    }

    // Create a blob from the input body
    if (request.body) {
      request
        .blob()
        .then((blob) => xhr.send(blob))
        .catch(reject)
      return
    }

    // Send empty request body
    xhr.send()
  })
}

/**
 * Options for {@link ApiClient._xhr}
 */
export interface XhrSendRequestOptions extends SendRequestOptions {
  /**
   * The form data to send.
   */
  readonly form?: FormData

  /**
   * Upload progress handler.
   */
  readonly onProgress?: (progress: { loaded: number; total: number }) => unknown
}

/**
 * Options for {@link ApiClient._send}.
 */
export interface SendRequestOptions {
  /**
   * If `true`, does not add an `Authorization` header to the request.
   */
  readonly unauthenticated?: boolean
}

/**
 * Common params used for page queries.
 */
export interface CursorPageQueryParams {
  /**
   * Token for the next page.
   */
  after?: string | null
  /**
   * Max records to return.
   */
  limit?: number
}

/**
 * A paged response with a cursor.
 */
export interface CursorPagedResponse<T> {
  /**
   * The data.
   */
  data: T[]
  /**
   * Total number of records, if available.
   */
  total?: number
  /**
   * Token for the next page.
   */
  cursor: string | null
}
